import * as React from "react";
import { Button } from "react-bootstrap";
import ReactToPrint from "react-to-print";
import { AdminApiController } from "../../../models/AdminApiController.generated";
import { UserDetails } from "../../../models/UserDetails";
import { UsersTable } from "./UsersTable";
import { Area } from "../../../models/Area";
import { UserArea } from "../../../models/UserArea";
import { UserModal } from "./UserModal"
import { PasswordModal } from "./PasswordModal";
import { LicenseType } from "../../../models/LicenseType";
import { Profile } from "../../../models/Profile";
import { hasEditUsersAccess } from "../shared/helpers";
import { AreaWithProjectName } from "../../../models/AreaWithProjectName";

type UserPageModals = undefined | "user" | "password"; 

interface Props {
  userProfile?: Profile;
  getUserProfile: () => void;
}

interface State {
  users: { [userId: number]: UserDetails };
  userAreas: UserArea[];
  areas: AreaWithProjectName[];
  selectedUserId?: number;
  shownModal?: UserPageModals;
  userModalErrorMessage?: string;
  pageErrorMessage?: string;
  ssoEnabled: boolean;
}

export class UsersPage extends React.Component<Props, State> {
  usersTblRef; //for print functionality

  constructor(props: Props) {
    super(props);
    this.state = {
      users: [],
      userAreas: [],
      areas: [],
      selectedUserId: null,
      ssoEnabled: false
    };

    this.usersTblRef = React.createRef();
  }

  componentDidMount() {
    AdminApiController.getUserList()
      .then(resp => {
        if (resp.success) {
          const users = resp.users.reduce((userDic, user) => {
            userDic[user.userID] = user;
            return userDic;
          }, {});
          this.setState({ users })
        } else {
          this.setState({ users: {}, pageErrorMessage: resp.message });
        }
      });

    AdminApiController.getAreas().then(resp => this.setState({ areas: resp.areas }));

    AdminApiController.getUserAreas().then(resp =>
      this.setState({ userAreas: resp.userAreas })
    );

    AdminApiController.ssoEnabled()
      .then(ssoEnabled => {
        this.setState({
          ssoEnabled: ssoEnabled
        });
      });
  }

  closeModal = () => {
    this.setState({
      shownModal: undefined,
      userModalErrorMessage: undefined
    });
  }

  createUser = (userDetails: UserDetails) => {
    AdminApiController.addUser(userDetails)
      .then(resp => {
        if (resp.result) {
          AdminApiController.getUserList().then(resp => {
            if (resp.success) {
              const users = resp.users.reduce((userDic, user) => {
                userDic[user.userID] = user;
                return userDic;
              }, {});
              this.setState({ users });
              this.closeModal();
            }
            else {
              this.setState({ users: {}, pageErrorMessage: resp.message });
              this.closeModal();
            }
          });
        } else {
          this.setState({
            userModalErrorMessage: resp.message
          });
        }
      });
  }

  updateUser = (userDetails: UserDetails) => {
    AdminApiController.updateUser(userDetails)
      .then(resp => {
        if (resp.result === true) {
          const users = { ... this.state.users };
          users[resp.user.userID] = resp.user;
          this.setState({ users, selectedUserId: resp.user.userID });
          this.closeModal();
          this.props.getUserProfile();
        } else {
          this.setState({
            userModalErrorMessage: resp.message
          });
        }
      });
  }

  updateUserPassword = (userId: number, newPassword: string) => {

      const newUser = new UserDetails();
      newUser.userID = userId;
      newUser.password = newPassword;

      AdminApiController.updateUserPassword(newUser)
        .then(updatedUser => {
          const users = { ... this.state.users };
          users[updatedUser.userID] = updatedUser;
          this.setState({
            users,
            selectedUserId: updatedUser.userID,
            shownModal: undefined
          })
        });
  }

  deactivateUser = (userId: number) => {
    AdminApiController.deactivateUser(userId)
      .then(user => {
        const users = { ... this.state.users };
        users[user.userID] = user;
        this.setState({
          users,
          selectedUserId: user.userID
        })
      });
  }

  getSelectedUser = () => {
    return this.state.selectedUserId
      ? this.state.users[this.state.selectedUserId]
      : null;
  }

  renderModals = () => {
    const shownModal = this.state.shownModal;
    if (shownModal == null) {
      return;
    }
    if (shownModal === "user") {
      return (
        <UserModal
          title={this.state.selectedUserId ? "Edit User" : "New User"}
          user={this.getSelectedUser()}
          errorMessage={this.state.userModalErrorMessage}
          createUser={this.createUser}
          updateUser={this.updateUser}
          closeModal={this.closeModal}
          users={this.state.users}
        />
      );
    }
    else if (shownModal === "password" && this.getSelectedUser() != null ) {
      const user = this.getSelectedUser();
      return (
        <PasswordModal
          title={`Change ${user.userName}'s Password`}
          user={user}
          submit={(password) => this.updateUserPassword(user.userID, password)}
          closeModal={this.closeModal} />
      )
    }
  }


  renderPrintButton() {
    return (
      <ReactToPrint
        trigger={() => <Button className="btn-success">Print</Button>}
        content={() => this.usersTblRef.current}
      />
    );
  }

  renderActionButtons() {
    return (
      <div className="flexCol" style={{ paddingLeft: "10px" }}>
        {this.renderPrintButton()}
        <Button
          variant="success"
          onClick={() => this.setState({
            selectedUserId: undefined,
            shownModal: "user"
          })}>
          Add
        </Button>
        <Button
          variant="success"
          onClick={() => this.setState({ shownModal: "user" })}
          disabled={this.state.selectedUserId == null}
        >
          Edit
        </Button>
        {!this.state.ssoEnabled ? (
          <Button
            variant="success"
            onClick={() => this.setState({ shownModal: "password" })}
            disabled={this.state.selectedUserId == null}
          >
            Edit Password
          </Button>) : null}
        <Button
          variant="success"
          onClick={() => this.deactivateUser(this.state.selectedUserId)}
          disabled={this.state.selectedUserId == null || this.getSelectedUser().license === LicenseType.Inactive}
        >
          Deactivate
        </Button>
      </div>
    );
  }

  render() {
    return (
      <div className="tab-inner">
        {this.renderModals()}
        <div className="flex">
          {this.state.pageErrorMessage ? (
            <div className="alert alert-danger" role="alert">{this.state.pageErrorMessage}</div>
          ): (
            <React.Fragment>
              <UsersTable
                users={this.state.users}
                userAreas={this.state.userAreas}
                areas={this.state.areas}
                onSelectUserRow={(selectedUser) => this.setState({ selectedUserId: selectedUser.userID })}
                ref={this.usersTblRef}
                userProfile={this.props.userProfile}
              />
              {hasEditUsersAccess(this.props.userProfile) && this.renderActionButtons()}
            </React.Fragment>
          )}
        </div>
      </div>
    );
  }
}
